/**
 * editor2/shared/InlineEditField.tsx
 * クリックでその場で編集できるフィールド
 * text, textarea, number, select, email, tel に対応
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useFieldMark, markBoxStyle, FieldMarkChip, type FieldMark } from '../conflict/fieldState';

interface SelectOption {
  label: string;
  value: string | number;
}

interface InlineEditFieldProps {
  label: string;
  value: any;
  field: string;
  onChange?: (updates: Record<string, any>) => void;
  disabled?: boolean;
  type?: 'text' | 'textarea' | 'number' | 'email' | 'tel' | 'select' | 'combobox';
  options?: SelectOption[];
  suffix?: string;
  displayValue?: string;
  /** select用: 選択肢のラベルではなく値のバッジ表示 */
  renderBadge?: (value: any) => React.ReactNode;
  placeholder?: string;
}

/**
 * 常時表示の候補付きテキスト入力（カスタムドロップダウン）。
 * - フォーカス／▼ボタンで候補ドロップダウンを表示（打鍵不要）
 * - 入力すると候補を部分一致でフィルタ
 * - 候補をクリック → 即セット & 閉じる & commit
 * - 外側クリック／Escape で閉じる
 * - blur / Enter で commit（フリー入力も許容）
 */
function ComboboxField({
  label,
  value,
  field,
  onChange,
  disabled,
  options,
  placeholder,
  mark,
}: Pick<
  InlineEditFieldProps,
  'label' | 'value' | 'field' | 'onChange' | 'disabled' | 'options' | 'placeholder'
> & { mark: FieldMark | null }) {
  const valueStr = value != null ? String(value) : '';
  const [localValue, setLocalValue] = useState<string>(valueStr);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 外部の value 変更を反映（別セクションからの更新等）
  useEffect(() => {
    setLocalValue(valueStr);
  }, [valueStr]);

  // 外側クリックで閉じる
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const commit = useCallback(
    (nextValue?: string) => {
      if (!onChange) return;
      const raw = nextValue !== undefined ? nextValue : localValue;
      const trimmed = raw.trim();
      const newValue = trimmed === '' ? null : trimmed;
      if (String(newValue ?? '') !== String(value ?? '')) {
        onChange({ [field]: newValue });
      }
    },
    [localValue, value, field, onChange],
  );

  // 候補を絞り込み（空なら全候補）
  const filteredOptions = useMemo(() => {
    if (!options || options.length === 0) return [];
    const query = localValue.trim().toLowerCase();
    if (!query) return options;
    return options.filter((opt) => {
      const v = String(opt.value).toLowerCase();
      const l = opt.label ? String(opt.label).toLowerCase() : '';
      return v.includes(query) || l.includes(query);
    });
  }, [options, localValue]);

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 28px 8px 12px',
    fontSize: '0.9rem',
    border: '1px solid #cbd5e1',
    borderRadius: 4,
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    background: disabled ? '#f8fafc' : '#fff',
    color: '#0f172a',
    fontWeight: 500,
    ...markBoxStyle(mark),
  };

  const hasOptions = filteredOptions.length > 0;

  return (
    <div
      className={`gc-field${mark ? ` gc-field--${mark}` : ''}`}
      data-field={field}
      ref={containerRef}
      style={{ position: 'relative' }}
    >
      {label && (
        <label
          className="gc-label"
          style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}
        >
          {label}
          <FieldMarkChip mark={mark} />
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={(e) => {
            setLocalValue(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // ドロップダウン外にフォーカスが移った場合のみ commit
            // （候補クリックは onMouseDown で preventDefault してblurさせない）
            commit();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
              setOpen(false);
              inputRef.current?.blur();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setOpen(false);
            } else if (e.key === 'ArrowDown') {
              if (!open) setOpen(true);
            }
          }}
          disabled={disabled}
          placeholder={placeholder}
          style={inputStyle}
          autoComplete="off"
        />
        <button
          type="button"
          onMouseDown={(e) => {
            // blur を防止（そうしないと onBlur が閉じてしまう）
            e.preventDefault();
            if (disabled) return;
            setOpen((prev) => !prev);
            inputRef.current?.focus();
          }}
          disabled={disabled}
          aria-label="候補を表示"
          style={{
            position: 'absolute',
            right: 4,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            padding: '4px 6px',
            fontSize: '0.7rem',
            color: '#64748b',
            cursor: disabled ? 'default' : 'pointer',
            lineHeight: 1,
          }}
        >
          ▼
        </button>
      </div>
      {open && hasOptions && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            background: '#fff',
            border: '1px solid #cbd5e1',
            borderRadius: 4,
            marginTop: 2,
            maxHeight: 240,
            overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
          }}
        >
          {filteredOptions.map((opt) => {
            const optValueStr = String(opt.value);
            const isSelected = optValueStr === localValue;
            return (
              <div
                key={optValueStr}
                role="option"
                aria-selected={isSelected}
                onMouseDown={(e) => {
                  // 候補クリックで input が blur しないよう抑止
                  e.preventDefault();
                  setLocalValue(optValueStr);
                  setOpen(false);
                  commit(optValueStr);
                  inputRef.current?.blur();
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background = '#f1f5f9';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background = isSelected
                    ? '#e0e7ff'
                    : 'transparent';
                }}
                style={{
                  padding: '8px 12px',
                  fontSize: '0.9rem',
                  color: '#0f172a',
                  cursor: 'pointer',
                  background: isSelected ? '#e0e7ff' : 'transparent',
                  fontWeight: isSelected ? 600 : 500,
                }}
              >
                {opt.label || optValueStr}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function InlineEditField({
  label,
  value,
  field,
  onChange,
  disabled = false,
  type = 'text',
  options,
  suffix,
  displayValue,
  renderBadge,
  placeholder,
}: InlineEditFieldProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);
  const markFields = useMemo(() => [field], [field]);
  const mark = useFieldMark(markFields);

  // 編集開始
  const startEdit = useCallback(() => {
    if (disabled) return;
    setEditValue(value != null ? String(value) : '');
    setEditing(true);
  }, [disabled, value]);

  // 編集確定
  const commitEdit = useCallback(() => {
    setEditing(false);
    if (!onChange) return;

    let newValue: any = editValue;
    if (type === 'number') {
      newValue = editValue === '' ? null : Number(editValue);
    }

    // 値が変わった場合のみonChange
    if (String(newValue ?? '') !== String(value ?? '')) {
      onChange({ [field]: newValue });
    }
  }, [editValue, value, field, onChange, type]);

  // キャンセル
  const cancelEdit = useCallback(() => {
    setEditing(false);
  }, []);

  // キーハンドラ
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && type !== 'textarea') {
        e.preventDefault();
        commitEdit();
      } else if (e.key === 'Escape') {
        cancelEdit();
      }
    },
    [commitEdit, cancelEdit, type],
  );

  // フォーカス
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement || inputRef.current instanceof HTMLTextAreaElement) {
        inputRef.current.select();
      }
    }
  }, [editing]);

  // select の場合は即座にコミット
  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newVal = e.target.value;
      setEditValue(newVal);
      setEditing(false);
      if (onChange) {
        onChange({ [field]: type === 'number' ? Number(newVal) : newVal });
      }
    },
    [onChange, field, type],
  );

  // 表示テキスト
  const getDisplayText = () => {
    if (displayValue != null) return displayValue;
    if (renderBadge) return null; // badgeが描画する
    if (type === 'select' && options) {
      const opt = options.find((o) => String(o.value) === String(value));
      if (opt) return opt.label;
    }
    if (value != null && value !== '') {
      if (type === 'number' && typeof value === 'number') {
        return `${value.toLocaleString()}${suffix ? ` ${suffix}` : ''}`;
      }
      return `${String(value)}${suffix ? ` ${suffix}` : ''}`;
    }
    return '-';
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    fontSize: '0.9rem',
    border: '2px solid #3b82f6',
    borderRadius: 4,
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  // combobox型は候補付きテキスト入力（フリー入力も可）
  if (type === 'combobox') {
    return (
      <ComboboxField
        label={label}
        value={value}
        field={field}
        onChange={onChange}
        disabled={disabled}
        options={options}
        placeholder={placeholder}
        mark={mark}
      />
    );
  }

  // select型は常にセレクト表示（クリックで切り替え不要）
  if (type === 'select' && options) {
    const selectStyle: React.CSSProperties = {
      width: '100%',
      padding: '8px 12px',
      fontSize: '0.9rem',
      border: '1px solid #e2e8f0',
      borderRadius: 4,
      outline: 'none',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
      background: disabled ? '#f8fafc' : '#fff',
      color: '#334155',
      cursor: disabled ? 'default' : 'pointer',
      ...markBoxStyle(mark),
    };

    // 現在値が options に含まれない場合（旧データ等）でも初期表示を維持するため、
    // その値を追加オプションとして表示する。
    const valueStr = value != null ? String(value) : '';
    const hasMatchingOption = options.some((opt) => String(opt.value) === valueStr);
    const showLegacyOption = valueStr !== '' && !hasMatchingOption;

    return (
      <div className={`gc-field${mark ? ` gc-field--${mark}` : ''}`} data-field={field}>
        {label && (
          <label
            className="gc-label"
            style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}
          >
            {label}
            <FieldMarkChip mark={mark} />
          </label>
        )}
        {renderBadge && (
          <div style={{ marginBottom: 4 }}>{renderBadge(value)}</div>
        )}
        <select
          value={valueStr}
          onChange={(e) => {
            const newVal = e.target.value;
            if (onChange) {
              onChange({ [field]: newVal === '' ? null : newVal });
            }
          }}
          disabled={disabled}
          style={selectStyle}
        >
          <option value="">-- 選択 --</option>
          {showLegacyOption && (
            <option value={valueStr}>{valueStr}（既存値）</option>
          )}
          {options.map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (editing) {
    return (
      <div className={`gc-field${mark ? ` gc-field--${mark}` : ''}`} data-field={field}>
        <label className="gc-label" style={{ fontSize: '0.85rem', color: '#3b82f6', fontWeight: 600 }}>
          {label}
          <FieldMarkChip mark={mark} />
        </label>
        {type === 'textarea' ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleKeyDown}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
            placeholder={placeholder}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type={type === 'number' ? 'number' : type === 'email' ? 'email' : type === 'tel' ? 'tel' : 'text'}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleKeyDown}
            style={inputStyle}
            placeholder={placeholder}
          />
        )}
      </div>
    );
  }

  return (
    <div className={`gc-field${mark ? ` gc-field--${mark}` : ''}`} data-field={field}>
      <label
        className="gc-label"
        style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}
      >
        {label}
        <FieldMarkChip mark={mark} />
      </label>
      <div
        className="gc-input"
        onClick={disabled ? undefined : startEdit}
        role={disabled ? undefined : 'button'}
        tabIndex={disabled ? undefined : 0}
        onKeyDown={
          disabled
            ? undefined
            : (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  startEdit();
                }
              }
        }
        style={{
          background: disabled ? '#f8fafc' : '#fff',
          border: '1px solid #cbd5e1',
          borderRadius: 4,
          padding: '8px 12px',
          minHeight: 38,
          display: 'flex',
          alignItems: 'center',
          color: '#0f172a',
          fontWeight: 500,
          cursor: disabled ? 'default' : 'pointer',
          transition: 'border-color 0.15s',
          ...markBoxStyle(mark),
        }}
      >
        <span style={{ flex: 1 }}>
          {renderBadge ? renderBadge(value) : getDisplayText()}
        </span>
      </div>
    </div>
  );
}
